import produce from 'immer';
import { intersectionBy } from 'lodash';
import pluralize from 'pluralize';
import { useCallback } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDispatch } from 'react-redux';

import { clipCanOpenInCanvas, getAddToCanvasMenuState } from '@air/feature-edit-canvas';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';
import { resetSelectedItemsAction, selectedItemsCountSelector } from '@air/redux-selected-items';

import { BatchExportImagesModal } from '~/components/ExportImagesModal/BatchExportImagesModal';
import { type GetSelectionMenuOptionsType } from '~/components/Gallery/types';
import { useAddAssetsToBoardsModal } from '~/components/Modals/AddAssetsToBoardsModal/useAddAssetsToBoardsModal';
import { useAddToCanvasModal } from '~/components/Modals/AddToCanvasModal/useAddToCanvasModal';
import { BulkDownloadAssetsModal } from '~/components/Modals/BulkDownloadAssetsModal';
import { useDuplicateBoard } from '~/components/Modals/DuplicateBoard/hooks/useDuplicateBoard';
import { EditClipsTagsModal } from '~/components/Modals/EditClipsTagsModal';
import { useMoveItemsModal } from '~/components/Modals/MoveItemsModal/useMoveItemsModal';
import { useRemoveAssetsFromBoardModal } from '~/components/Modals/RemoveClipsFromBoardModal/hooks/useRemoveAssetsFromBoardModal';
import { PrivateContextSourcesAddClipModal } from '~/components/v2/components/context-sources/modals/PrivateContextSourcesAddClipModal';
import { useWorkspaceAgentSelectionMenuHeroOptions } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentSelectionMenuHeroOptions';
import { useCanEditContextSources } from '~/components/v2/hooks/useCanEditContextSources';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useCanCreateCanvasInCurrentLocation } from '~/hooks/canvases/useCanCreateCanvasInCurrentLocation';
import { useOpenClipInCanvas } from '~/hooks/canvases/useOpenClipInCanvas';
import { usePrivateEditInCanvasMenuOption } from '~/hooks/canvases/usePrivateEditInCanvasMenuOption';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { useAddToShowcaseOption } from '~/hooks/useAddToShowcaseOption';
import { useAssetPermissionsCache } from '~/hooks/useAssetPermissionsCache';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useBulkDownloadPrivateAssets } from '~/hooks/useBulkDownloadPrivateAssets';
import { useClipPermissionsCache } from '~/hooks/useClipPermissionsCache';
import { useDownloadPrivateItems } from '~/hooks/useDownloadPrivateItems';
import { useGetPrivateCustomFieldsMenuOptions } from '~/hooks/useGetCustomFieldsMenuOptions';
import { useHandleDeletePrivateSelection } from '~/hooks/useHandleDeletePrivateSelection';
import { useIgnoreSelectionDeleteShortcut } from '~/hooks/useIgnoreSelectionDeleteShortcut';
import { usePrivateSelectAllBoardMembershipActions } from '~/hooks/usePrivateSelectAllBoardMembershipActions';
import { useStartPrivateAssetComparison } from '~/hooks/useStartPrivateAssetComparison';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { CentralizedBoardSelectors, centralizedBoardTitleSelector } from '~/store/centralizedBoard/selectors';
import { isFlattenedViewSelector } from '~/store/router/selectors';
import {
  hasOnlyBoardsSelectedSelector,
  hasOnlyClipsSelectedSelector,
  hasSelectionAcrossMultipleViewsSelector,
  selectedBoardIdsForShowcaseSelector,
  selectedBoardsIdsSelector,
  selectedBoardsSelector,
  selectedClipsAssetIdsSelector,
  selectedClipsHasUnfavorited,
  selectedClipsIdsInfoSelector,
  selectedClipsSelector,
  selectedMediaClipsSelector,
  selectedPrivateBoardsSelector,
} from '~/store/selectedItems/selectors';
import { useGetBoardContainerPermissions } from '~/swr-hooks/boards/useGetBoardContainerPermissions';
import { useToggleFavorite } from '~/swr-hooks/clips/useToggleFavorite';
import { useActiveSubscriptionCallback } from '~/swr-hooks/subscriptions/useActiveSubscriptionCallback';
import { canCompareSelection } from '~/utils/canCompareSelection';
import {
  getBatchCropAndEditOnCanvasHandler,
  getBatchCropAndEditOnCanvasOpenedFrom,
} from '~/utils/canvases/getCropAndEditOnCanvasHandler';
import { getEditInCanvasOpenedFrom } from '~/utils/canvases/getEditInCanvasAnalytics';
import { isClipExportable } from '~/utils/Imgix';
import { getDropdownMenuOption } from '~/utils/menuOptions/getDropdownMenuOption';
import {
  canAddAssetToBoard,
  canChangeAssetCustomFields,
  canDeleteAsset,
  canMoveAsset,
  canSeeApplicableClipTags,
} from '~/utils/permissions/assetPermissions';
import {
  canChangeBoardCustomFields,
  canDeleteBoard,
  canDuplicateBoard,
  canRemoveAssetsFromBoard,
} from '~/utils/permissions/boardPermissions';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';
import { useAirStore } from '~/utils/ReduxUtils';

const emptyArray: DropdownMenuOption[] = [];

export const usePrivateSelectionMenuOptions = () => {
  const { isSearchActive } = useIsSearchActive();
  const { currentWorkspace } = useCurrentWorkspace();

  const [showBatchExportImagesModal] = useAirModal(BatchExportImagesModal);
  const [showEditTagsToClipsModal] = useAirModal(EditClipsTagsModal);
  const { showRemoveAssetsFromBoardModal } = useRemoveAssetsFromBoardModal();
  const { showAddAssetsToBoardsModal } = useAddAssetsToBoardsModal();
  const { showDuplicateBoardsModal } = useDuplicateBoard();
  const { showMoveItemsModal } = useMoveItemsModal();
  const { toggleFavorite } = useToggleFavorite();
  const { activeSubscriptionCallback } = useActiveSubscriptionCallback();
  const { getCustomFieldsMenuOptions } = useGetPrivateCustomFieldsMenuOptions();
  const { applyMultiClipAgentMenuTransforms, customFieldsSubmenuClassName, enrichCustomFieldsMenuOptions } =
    useWorkspaceAgentSelectionMenuHeroOptions();
  const getAddToShowcaseOption = useAddToShowcaseOption();
  const { showAddToCanvasModal } = useAddToCanvasModal();
  const [showAddContextSourceClipModal] = useAirModal(PrivateContextSourcesAddClipModal);
  const { canEditContextSources } = useCanEditContextSources();
  const { startPrivateAssetComparison } = useStartPrivateAssetComparison();
  const { downloadPrivateItems } = useDownloadPrivateItems();
  const { bulkDownloadPrivateAssets } = useBulkDownloadPrivateAssets();
  const [showBulkDownloadAssetsModal] = useAirModal(BulkDownloadAssetsModal);
  const store = useAirStore();
  const dispatch = useDispatch();
  const { getClipPermissions } = useClipPermissionsCache();
  const { getBoardPermissions } = useBoardPermissionsCache();
  const { getAssetPermissions } = useAssetPermissionsCache();
  const { getBoardContainerPermissions } = useGetBoardContainerPermissions();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const { canCreateCanvas } = useCanCreateCanvasInCurrentLocation();
  const { openClipInCanvas } = useOpenClipInCanvas();
  const { getEditInCanvasMenuOption } = usePrivateEditInCanvasMenuOption();
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const {
    canRunBoardMembershipAction: canRunSelectAllBoardMembershipAction,
    openAddToBoardModal: openSelectAllAddToBoardModal,
    openMoveItemsModal: openSelectAllMoveItemsModal,
    selectAllTotalCount,
  } = usePrivateSelectAllBoardMembershipActions();
  const handleDelete = useHandleDeletePrivateSelection();
  const { ignoreSelectionDeleteShortcutRef } = useIgnoreSelectionDeleteShortcut();

  const getSelectionMenuOptions: GetSelectionMenuOptionsType = useCallback(
    (trackLocation) => {
      const currentBoardId = CentralizedBoardSelectors.boardId(store.getState());
      const currentBoardPermissions = currentBoardId ? getBoardPermissions(currentBoardId) : undefined;
      const boardTitle = centralizedBoardTitleSelector(store.getState());
      const isFlattenedView = isFlattenedViewSelector(store.getState()) === 'true';

      const hasOnlyClipsSelected = hasOnlyClipsSelectedSelector(store.getState());
      const hasOnlyBoardsSelected = hasOnlyBoardsSelectedSelector(store.getState());
      const areAllClipsFavorited = !selectedClipsHasUnfavorited(store.getState());
      const selectedCount = selectedItemsCountSelector(store.getState());
      const selectedClipsInfo = selectedClipsIdsInfoSelector(store.getState());
      const selectedClipIds = selectedClipsInfo.map(({ id }) => id);
      const selectedBoards = selectedBoardsSelector(store.getState());
      const selectedPrivateBoards = selectedPrivateBoardsSelector(store.getState());
      const selectedBoardsIds = selectedBoardsIdsSelector(store.getState());
      const selectedClips = selectedClipsSelector(store.getState());
      const selectedMediaClips = selectedMediaClipsSelector(store.getState());
      const hasSelectionAcrossMultipleViews = hasSelectionAcrossMultipleViewsSelector(store.getState());

      const canMoveAllBoards = selectedBoards.every((board) => canDeleteBoard(getBoardContainerPermissions({ board })));

      const canDownloadAllClips = selectedClips.every(({ id }) => canGenerateZip(getClipPermissions(id)));
      const canDownloadAllBoards = selectedBoards.every(({ id }) => canGenerateZip(getBoardPermissions(id)));

      const canEditSelectClipsTags = selectedClips.every(({ id }) => canSeeApplicableClipTags(getClipPermissions(id)));

      const canMoveAllAssets = selectedClips.every(({ assetId }) => canMoveAsset(getAssetPermissions(assetId)));
      const canEditAllClipCustomFields = selectedClips.every(({ id }) =>
        canChangeAssetCustomFields(getClipPermissions(id)),
      );
      const canEditAllBoardCustomFields = selectedBoards.every(({ id }) =>
        canChangeBoardCustomFields(getBoardPermissions(id)),
      );

      const canEditCustomFields = canEditAllClipCustomFields && canEditAllBoardCustomFields;
      const canRemoveAssets = currentBoardId && canRemoveAssetsFromBoard(currentBoardPermissions);

      if (selectedCount < 2) {
        return emptyArray;
      }

      const options = produce<DropdownMenuOption[]>([], (draft) => {
        const addToSubItems: DropdownMenuOption[] = [];

        // Select All operates on the source board, rather than only the loaded clips.
        if (selectAll) {
          if (canRunSelectAllBoardMembershipAction) {
            draft.push(
              {
                ...getDropdownMenuOption('add-to-board'),
                onSelect: () => openSelectAllAddToBoardModal(trackLocation),
                type: 'item',
              },
              {
                ...getDropdownMenuOption('move'),
                onSelect: () => openSelectAllMoveItemsModal(trackLocation),
                type: 'item',
              },
            );
          }

          let hasEditSeparator = false;
          const addEditSeparator = () => {
            if (!hasEditSeparator && draft.length > 0) {
              draft.push({ id: 'edit-separator', type: 'separator' });
            }
            hasEditSeparator = true;
          };

          if (hasOnlyClipsSelected && canEditSelectClipsTags) {
            addEditSeparator();
            draft.push({
              ...getDropdownMenuOption('edit-tags'),
              onSelect: () => {
                showEditTagsToClipsModal({
                  clipIds: selectedClipIds,
                  trackLocation,
                  initialTags: [],
                });
              },
              type: 'item',
            });
          }

          if (hasOnlyClipsSelected && canEditCustomFields && getCustomFieldsMenuOptions) {
            const customFieldsMenuOptions = getCustomFieldsMenuOptions({
              assets: selectedClips,
            });

            if (customFieldsMenuOptions.length > 0) {
              addEditSeparator();
              draft.push({
                ...getDropdownMenuOption('edit-custom-fields'),
                className: customFieldsSubmenuClassName,
                options: enrichCustomFieldsMenuOptions(customFieldsMenuOptions),
                type: 'sub',
              });
            }
          }

          // Add header with count
          if (draft.length > 0) {
            const displayCount =
              (selectAllTotalCount ?? 0) > selectedCount ? (selectAllTotalCount ?? 0) : selectedCount;
            draft.unshift(
              {
                id: 'selected-header',
                type: 'title',
                label: `${pluralize('item', displayCount, true)} selected`,
              },
              {
                id: 'selected-separator',
                type: 'separator',
              },
            );
          }

          return draft;
        }

        if (hasOnlyClipsSelected) {
          const handleFavorite = () =>
            activeSubscriptionCallback(() => toggleFavorite({ clips: selectedClips, trackLocation }));

          if (areAllClipsFavorited) {
            draft.push({
              ...getDropdownMenuOption('unfavorite'),
              onSelect: handleFavorite,
              type: 'item',
            });
          } else {
            draft.push({
              ...getDropdownMenuOption('favorite'),
              onSelect: handleFavorite,
              type: 'item',
            });
          }

          const canAddAllAssetsToABoard = selectedClips.every(({ assetId }) =>
            canAddAssetToBoard(getAssetPermissions(assetId)),
          );

          if (canAddAllAssetsToABoard) {
            addToSubItems.push({
              ...getDropdownMenuOption('add-to-board'),
              onSelect: () =>
                showAddAssetsToBoardsModal({
                  clipIds: selectedClipIds,
                  trackLocation,
                  parentBoardId: currentBoardId,
                }),
              type: 'item',
            });
          }

          const { canAdd: canAddSelectedClipsToCanvas, disabledReason: addToCanvasDisabledReason } =
            getAddToCanvasMenuState(selectedClips);

          addToSubItems.push({
            ...getDropdownMenuOption('add-to-canvas'),
            description: addToCanvasDisabledReason,
            disabled: !canAddSelectedClipsToCanvas,
            ...(canAddSelectedClipsToCanvas ? { onSelect: () => showAddToCanvasModal({ clips: selectedClips }) } : {}),
            type: 'item',
          });

          if (canEditContextSources && selectedClips.length === 1) {
            addToSubItems.push({
              ...getDropdownMenuOption('add-to-context'),
              onSelect: () => showAddContextSourceClipModal({ clip: selectedClips[0] }),
              type: 'item',
            });
          }
        }

        if (
          canCompareSelection({
            hasSelectionAcrossMultipleViews,
            isSelectAllActive: selectAll,
            selectedItemCount: selectedCount,
            selectedMediaClips,
          })
        ) {
          const [primaryClip, comparisonClip] = selectedMediaClips;

          draft.push({
            ...getDropdownMenuOption('compare'),
            onSelect: () =>
              startPrivateAssetComparison({
                comparisonClipId: comparisonClip.id,
                isAssetModalAlreadyOpen: false,
                primaryClip,
              }),
            type: 'item',
          });
        }

        if (canDownloadAllClips && canDownloadAllBoards) {
          draft.push({
            ...getDropdownMenuOption('download'),
            onSelect: () => {
              if (selectedBoardsIds.length > 0) {
                void downloadPrivateItems({ trackLocation, boardIds: selectedBoardsIds, clipIds: selectedClipIds });
              } else {
                showBulkDownloadAssetsModal({
                  clipIds: selectedClipIds,
                  onDownloadAsZip: () => {
                    void downloadPrivateItems({ trackLocation, clipIds: selectedClipIds });
                    dispatch(resetSelectedItemsAction());
                  },
                  onDownloadIndividually: () => {
                    bulkDownloadPrivateAssets({ clipIds: selectedClipIds, trackLocation });
                    dispatch(resetSelectedItemsAction());
                  },
                });
              }
            },
            type: 'item',
          });
        }

        if (hasOnlyClipsSelected) {
          const hasExportableClip = selectedClips.some((clip) => isClipExportable(clip));

          if (hasExportableClip) {
            draft.push({
              ...getDropdownMenuOption('export-as'),
              label: 'Export selection as',
              onSelect: () =>
                showBatchExportImagesModal({
                  clips: selectedClips,
                  onCropAndEditOnCanvas: getBatchCropAndEditOnCanvasHandler({
                    canCreateCanvas,
                    clips: selectedClips,
                    openClipInCanvas,
                    openedFrom: getBatchCropAndEditOnCanvasOpenedFrom(trackLocation),
                  }),
                  workspaceId: currentWorkspace?.id,
                }),
              type: 'item',
            });
          }
        }

        if (getAddToShowcaseOption) {
          const addToShowcaseOption = getAddToShowcaseOption({
            assetIds: selectedClipsAssetIdsSelector(store.getState()),
            boardIds: selectedBoardIdsForShowcaseSelector(store.getState()),
          });

          if (addToShowcaseOption) {
            addToSubItems.push(addToShowcaseOption);
          }
        }

        if (addToSubItems.length > 0) {
          if (draft.length > 0) {
            draft.push({ id: 'add-to-separator', type: 'separator' });
          }
          draft.push({ ...getDropdownMenuOption('add-to'), options: addToSubItems, type: 'sub' });
        }

        if (hasOnlyClipsSelected && canCreateCanvas) {
          const clipsToOpenInCanvas = selectedClips.filter(clipCanOpenInCanvas);

          if (clipsToOpenInCanvas.length > 0) {
            if (draft.length > 0) {
              draft.push({ id: 'edit-in-canvas-separator', type: 'separator' });
            }

            const openedFrom = getEditInCanvasOpenedFrom(trackLocation);

            draft.push(
              getEditInCanvasMenuOption({
                canOfferSizeSet: false,
                clips: clipsToOpenInCanvas,
                onSelect: (selection) => openClipInCanvas({ ...selection, clips: selectedClips, openedFrom }),
                openedFrom,
              }),
            );
          }
        }

        const canMove = !!currentBoardId && !isSearchActive;

        /**
         * 'Move' option should show if user is in search and boards and assets are selected,
         * or if anything is selected while selecting in a board
         */
        const shouldShowMoveOption = (canMoveAllBoards && !hasOnlyClipsSelected && !canMove) || canMove;

        if (!isFlattenedView && shouldShowMoveOption && canMoveAllAssets && canMoveAllBoards && currentBoardId) {
          draft.push({
            ...getDropdownMenuOption('move'),
            onSelect: () =>
              showMoveItemsModal({
                boards: selectedPrivateBoards,
                clips: selectedClipsInfo,
                onMove: () => dispatch(resetSelectedItemsAction()),
                trackLocation,
              }),
            type: 'item',
          });
        }

        const hasEditSection =
          (hasOnlyClipsSelected && canEditSelectClipsTags) || (canEditCustomFields && !!getCustomFieldsMenuOptions);

        if (hasEditSection) {
          if (draft.length > 0) {
            draft.push({ id: 'edit-separator', type: 'separator' });
          }

          if (hasOnlyClipsSelected && canEditSelectClipsTags) {
            draft.push({
              ...getDropdownMenuOption('edit-tags'),
              onSelect: () => {
                const commonTags = intersectionBy(...selectedClips.map(({ tags }) => tags ?? []), 'id');
                showEditTagsToClipsModal({
                  clipIds: selectedClipIds,
                  trackLocation,
                  initialTags: commonTags,
                });
              },
              type: 'item',
            });
          }

          if (canEditCustomFields && getCustomFieldsMenuOptions) {
            const customFieldsMenuOptions = getCustomFieldsMenuOptions({
              boards: selectedBoards,
              assets: selectedClips,
            });

            if (customFieldsMenuOptions.length) {
              draft.push({
                ...getDropdownMenuOption('edit-custom-fields'),
                className: customFieldsSubmenuClassName,
                options: enrichCustomFieldsMenuOptions(customFieldsMenuOptions),
                type: 'sub',
              });
            }
          }
        }

        const canDuplicateAllBoards = selectedBoards.every((board) =>
          canDuplicateBoard({
            boardContext: getBoardPermissions(board.id),
            targetContext: getBoardContainerPermissions({ board }),
          }),
        );

        if (hasOnlyBoardsSelected && canDuplicateAllBoards) {
          draft.push({
            ...getDropdownMenuOption('duplicate'),
            onSelect: () => showDuplicateBoardsModal({ boards: selectedPrivateBoards, trackLocation }),
            type: 'item',
          });
        }

        const isRemoveFromBoardVisible =
          hasOnlyClipsSelected && !isFlattenedView && currentBoardId && boardTitle && canRemoveAssets;

        if (isRemoveFromBoardVisible) {
          draft.push({
            id: 'remove-separator',
            type: 'separator',
          });

          draft.push({
            ...getDropdownMenuOption('remove-from-board'),
            onSelect: () =>
              showRemoveAssetsFromBoardModal({
                boardId: currentBoardId,
                boardTitle,
                clipIds: selectedClipIds,
                trackLocation,
              }),
            type: 'item',
          });
        }

        const canDeleteAllAssets =
          selectedClips.length > 0
            ? canDeleteAsset(currentBoardId ? currentBoardPermissions : workspacePermissions)
            : true;
        const canDeleteAllBoards =
          selectedBoards.length > 0
            ? selectedBoards.every((board) => canDeleteBoard(getBoardContainerPermissions({ board })))
            : true;

        if (canDeleteAllAssets && canDeleteAllBoards) {
          if (!isRemoveFromBoardVisible) {
            draft.push({
              id: 'delete-separator',
              type: 'separator',
            });
          }

          draft.push({
            ...getDropdownMenuOption('delete'),
            onSelect: () => handleDelete(trackLocation),
            type: 'item',
          });
        }

        if (draft.length && selectedCount > 1) {
          // Use selectAllTotalCount when select-all is active and there are unloaded items
          const displayCount =
            selectAll && (selectAllTotalCount ?? 0) > selectedCount ? (selectAllTotalCount ?? 0) : selectedCount;
          draft.unshift(
            {
              id: 'selected-header',
              type: 'title',
              label: `${pluralize('item', displayCount, true)} selected`,
            },
            {
              id: 'selected-separator',
              type: 'separator',
            },
          );
        }

        return draft;
      });

      return applyMultiClipAgentMenuTransforms(options, { hasOnlyClipsSelected, selectedCount });
    },
    [
      store,
      applyMultiClipAgentMenuTransforms,
      customFieldsSubmenuClassName,
      enrichCustomFieldsMenuOptions,
      getBoardPermissions,
      getBoardContainerPermissions,
      getClipPermissions,
      getAssetPermissions,
      isSearchActive,
      getCustomFieldsMenuOptions,
      workspacePermissions,
      canCreateCanvas,
      openClipInCanvas,
      getEditInCanvasMenuOption,
      activeSubscriptionCallback,
      toggleFavorite,
      showAddAssetsToBoardsModal,
      showBatchExportImagesModal,
      showEditTagsToClipsModal,
      downloadPrivateItems,
      dispatch,
      showBulkDownloadAssetsModal,
      bulkDownloadPrivateAssets,
      showMoveItemsModal,
      showDuplicateBoardsModal,
      showRemoveAssetsFromBoardModal,
      handleDelete,
      getAddToShowcaseOption,
      showAddToCanvasModal,
      showAddContextSourceClipModal,
      canEditContextSources,
      selectAll,
      selectAllTotalCount,
      canRunSelectAllBoardMembershipAction,
      openSelectAllAddToBoardModal,
      openSelectAllMoveItemsModal,
      startPrivateAssetComparison,
      currentWorkspace?.id,
    ],
  );

  useHotkeys(
    'Backspace, delete, del',
    () => {
      handleDelete('keyboard');
    },
    {
      ignoreEventWhen: ignoreSelectionDeleteShortcutRef.current,
      enableOnFormTags: false,
    },
    [handleDelete],
  );

  return {
    getSelectionMenuOptions,
  };
};
